import { PricingRule, AvailabilityRule } from '@/types/revenue-manager';

/**
 * Transform imported rule to API payload format for batch import
 */
export interface ImportRulePayload {
  id?: string;
  name: string;
  org_id: string;
  property_id: string;
  priority: number;
  isActive: boolean;
  ruletype: 'restriction' | 'availability';
  conditions: Array<{
    type: string;
    operator: string;
    value: number | string;
    logicOperator: string | null;
  }>;
  action?: {
    adjustmentType?: 'percentage' | 'fixed';
    adjustmentValue?: number;
    applyTo?: string;
    actionType?: string;
    actionValue?: string | number;
  };
  roomTypes: number[];
  roomVariants: number[];
  otaChannels: string[];
  applicable_cm_names?: string[];
  applicableTo: {
    startDate: string;
    endDate?: string;
    daysOfWeek?: string[];
    timeOfDay?: string;
  };
  executionType: {
    processingFrequency: string;
    executionDays: number[];
    executionTime?: string;
    executionEndDate?: string;
  };
  submitted_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * Convert day of week number to string
 */
function dayOfWeekToString(day: number): string {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[day] || 'sunday';
}

/**
 * Transform pricing rule to import payload format
 */
export function transformPricingRuleForImport(
  rule: Partial<PricingRule>,
  orgId: string,
  propertyId: string,
  submittedBy: string
): ImportRulePayload {
  const now = new Date().toISOString();
  
  // Transform conditions
  const conditions = (rule.conditions || []).map((condition, index, array) => ({
    type: condition.type === 'daysUntilCheckIn' ? 'daysBefore' : condition.type,
    operator: condition.operator,
    value: condition.value,
    logicOperator: index < array.length - 1 ? (condition.logic || 'AND') : null,
  }));

  // Convert room types to numbers
  const roomTypes = (rule.roomTypes || []).map(rt => {
    const num = typeof rt === 'string' ? parseInt(rt, 10) : rt;
    return isNaN(num) ? 0 : num;
  });

  // Convert room variants to numbers
  const roomVariants = (rule.selectedVariants || []).map(sv => {
    const num = typeof sv.variantId === 'string' ? parseInt(sv.variantId, 10) : sv.variantId;
    return isNaN(num) ? 0 : num;
  });

  // Convert days of week to string array
  const daysOfWeek = (rule.daysOfWeek || []).map(dayOfWeekToString);

  return {
    name: rule.name || 'Imported Rule',
    org_id: orgId,
    property_id: propertyId,
    priority: rule.priority || 1,
    isActive: rule.isActive ?? false,
    ruletype: 'restriction',
    conditions,
    action: {
      adjustmentType: rule.adjustmentType || 'percentage',
      adjustmentValue: rule.adjustmentValue || 0,
      applyTo: rule.applyTo === 'basePrice' ? 'base_price' : 'current_price',
    },
    roomTypes,
    roomVariants,
    otaChannels: rule.otaChannels || [],
    applicable_cm_names: (rule as any).applicateCmNames || (rule as any).applicable_cm_names || [],
    applicableTo: {
      startDate: rule.startDate || new Date().toISOString().split('T')[0],
      endDate: rule.endDate,
      daysOfWeek: daysOfWeek.length > 0 ? daysOfWeek : undefined,
      timeOfDay: rule.timeOfDay || null,
    },
    executionType: {
      processingFrequency: rule.processingFrequency || 'manual',
      executionDays: rule.executionDays || [],
      executionTime: rule.executionTime,
      executionEndDate: rule.executionEndDate,
    },
    submitted_by: submittedBy,
    created_at: now,
    updated_at: now,
  };
}

/**
 * Transform availability rule to import payload format
 */
export function transformAvailabilityRuleForImport(
  rule: Partial<AvailabilityRule>,
  orgId: string,
  propertyId: string,
  submittedBy: string
): ImportRulePayload {
  const now = new Date().toISOString();

  // Convert room types to numbers
  const roomTypes = (rule.roomTypes || []).map(rt => {
    const num = typeof rt === 'string' ? parseInt(rt, 10) : rt;
    return isNaN(num) ? 0 : num;
  });

  // Convert room variants to numbers
  const roomVariants = (rule.selectedVariants || []).map(sv => {
    const num = typeof sv.variantId === 'string' ? parseInt(sv.variantId, 10) : sv.variantId;
    return isNaN(num) ? 0 : num;
  });

  // Convert days of week to string array
  const daysOfWeek = (rule.daysOfWeek || []).map(dayOfWeekToString);

  return {
    name: rule.name || 'Imported Rule',
    org_id: orgId,
    property_id: propertyId,
    priority: rule.priority || 1,
    isActive: rule.isActive ?? false,
    ruletype: 'availability',
    conditions: [], // Availability rules typically don't have conditions
    action: {
      actionType: rule.actionType || 'setStatus',
      actionValue: rule.actionValue,
    },
    roomTypes,
    roomVariants,
    otaChannels: rule.otaChannels || [],
    applicable_cm_names: (rule as any).applicateCmNames || (rule as any).applicable_cm_names || [],
    applicableTo: {
      startDate: rule.startDate || new Date().toISOString().split('T')[0],
      endDate: rule.endDate,
      daysOfWeek: daysOfWeek.length > 0 ? daysOfWeek : undefined,
      timeOfDay: rule.timeOfDay || null,
    },
    executionType: {
      processingFrequency: rule.processingFrequency || 'manual',
      executionDays: rule.executionDays || [],
      executionTime: rule.executionTime,
      executionEndDate: rule.executionEndDate,
    },
    submitted_by: submittedBy,
    created_at: now,
    updated_at: now,
  };
}
